import { useState, useCallback, useMemo, useEffect } from "react";
import { Drawer } from "./Drawer";
import { Select } from "../atoms/Select";
import { MultiSelect } from "../molecules/MultiSelect";
import { FileUpload } from "../molecules/FileUpload";
import { Button } from "../atoms/Button";
import { useBusinessUnits } from "../../hooks/useBusinessUnits";
import { useContractCategories } from "../../hooks/useContractCategories";
import type { TemplateFormData, TemplateFormErrors } from "../../types/templates";
import { DEFAULT_BUSINESS_UNIT_NAME } from "../../constants/defaultBusinessUnit";
import {
  ALL_BUSINESS_UNITS_SENTINEL,
  resolveBusinessUnitsForApi,
} from "../../utils/businessUnitApiCodes";

type CreateTemplateDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onCreate: (formData: TemplateFormData) => Promise<void>;
};

const emptyForm: TemplateFormData = {
  templateCategory: "Contract",
  contractCategory: "",
  businessUnits: [],
  file: null,
};

const validate = (formData: TemplateFormData): TemplateFormErrors => {
  const errors: TemplateFormErrors = {};

  if (!formData.contractCategory) {
    errors.contractCategory = "Contract category is required.";
  }
  if (formData.businessUnits.length === 0) {
    errors.businessUnits = "At least one business unit is required.";
  }
  if (!formData.file) {
    errors.file = "Please upload a file.";
  }

  return errors;
};

export const CreateTemplateDrawer = ({ isOpen, onClose, onCreate }: CreateTemplateDrawerProps) => {
  const { options } = useBusinessUnits();
  const businessUnitNameOptions = useMemo(
    () => options.filter((o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code).map((o) => o.name),
    [options],
  );
  const { categories: contractCategories, loading: categoriesLoading } = useContractCategories();
  const [formData, setFormData] = useState<TemplateFormData>(emptyForm);
  const [errors, setErrors] = useState<TemplateFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isFormValid = useMemo(
    () =>
      formData.contractCategory !== "" &&
      formData.businessUnits.length > 0 &&
      formData.file !== null,
    [formData],
  );

  const defaultBusinessUnits = useMemo(
    () =>
      businessUnitNameOptions.includes(DEFAULT_BUSINESS_UNIT_NAME)
        ? [DEFAULT_BUSINESS_UNIT_NAME]
        : [],
    [businessUnitNameOptions],
  );

  const resetForm = useCallback(() => {
    setFormData({
      ...emptyForm,
      businessUnits: defaultBusinessUnits,
    });
    setErrors({});
    setIsSubmitting(false);
    setSubmitError(null);
  }, [defaultBusinessUnits]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setFormData((prev) => {
      if (prev.businessUnits.length > 0) {
        return prev;
      }
      if (!businessUnitNameOptions.includes(DEFAULT_BUSINESS_UNIT_NAME)) {
        return prev;
      }
      return { ...prev, businessUnits: [DEFAULT_BUSINESS_UNIT_NAME] };
    });
  }, [isOpen, businessUnitNameOptions]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validate(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      requestAnimationFrame(() => {
        if (validationErrors.contractCategory) {
          document.getElementById("create-template-contract-category")?.focus();
        } else if (validationErrors.businessUnits) {
          document.getElementById("multiselect-business-unit")?.focus();
        } else if (validationErrors.file) {
          document.getElementById("file-upload-upload-file")?.focus();
        }
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await onCreate({
        ...formData,
        businessUnits: resolveBusinessUnitsForApi(formData.businessUnits, options),
      });
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Template creation failed";
      setSubmitError(message);
      setIsSubmitting(false);
    }
  }, [formData, onCreate, resetForm, options]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title="Add Template">
      <div className="flex min-h-full flex-col">
        <div className="drawer-form flex-1">
          <Select
            id="create-template-contract-category"
            label="Contract Category"
            options={[...contractCategories]}
            value={formData.contractCategory}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, contractCategory: e.target.value }));
              setErrors((prev) => ({ ...prev, contractCategory: undefined }));
            }}
            error={errors.contractCategory}
            required
            disabled={categoriesLoading}
            placeholder={categoriesLoading ? "Loading categories…" : "Select contract category"}
          />

          {/* Business Unit — dropdown multi-select with chips */}
          <MultiSelect
            label="Business Unit"
            options={businessUnitNameOptions}
            value={formData.businessUnits}
            onChange={(selected) => {
              setFormData((prev) => ({ ...prev, businessUnits: selected }));
              setErrors((prev) => ({ ...prev, businessUnits: undefined }));
            }}
            error={errors.businessUnits}
            required
            placeholder="Select business units"
          />

          {/* Upload File */}
          <FileUpload
            label="Upload File"
            value={formData.file}
            onChange={(file) => {
              setFormData((prev) => ({ ...prev, file }));
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
            error={errors.file}
            required
            accept=".pdf,.doc,.docx"
            maxSizeMB={10}
            helperText="Word document (Max 10MB)"
          />

          {(!isFormValid || submitError) && (
            <div className="drawer-form-validation-banner">
              {!isFormValid && (
                <p className="drawer-form-validation-banner__text">
                  Please complete the required fields above to continue
                </p>
              )}
              {submitError && <p className="text-sm text-status-error">{submitError}</p>}
            </div>
          )}
        </div>

        <div className="drawer-form-footer">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? "Adding..." : "Add Template"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
